import { Check } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

const MenuContext = createContext({ close: () => {} });

const Wrapper = styled.div`
  display: inline-flex;
  position: relative;
`;

const List = styled.div`
  background-color: var(--popover);
  border-radius: var(--radius-lg);
  /* Without color-mix() the next declaration is dropped whole: keep an outline and a shadow. */
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-md);
  box-shadow:
    0 0 0 1px color-mix(in oklab, var(--foreground) 10%, transparent),
    var(--shadow-md);
  box-sizing: border-box;
  color: var(--popover-foreground);
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: min(80vh, 560px);
  max-width: calc(100vw - 32px);
  min-width: 160px;
  overflow-y: auto;
  padding: 4px;
  position: absolute;
  z-index: 60;

  ${({ $align }) =>
    $align === 'start'
      ? css`
          left: 0;
        `
      : css`
          right: 0;
        `};

  ${({ $side }) =>
    $side === 'top'
      ? css`
          bottom: calc(100% + 6px);
        `
      : css`
          top: calc(100% + 6px);
        `};
`;

const Item = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  border-radius: var(--radius-md);
  box-sizing: border-box;
  color: inherit;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  gap: 8px;
  line-height: 20px;
  margin: 0;
  min-height: 36px;
  outline: none;
  padding: 6px 10px;
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
  width: 100%;

  svg {
    flex-shrink: 0;
    pointer-events: none;
  }

  &:hover,
  &:focus-visible {
    background-color: var(--accent);
    color: var(--accent-foreground);
  }

  &[aria-checked='true'],
  &[aria-current='true'] {
    font-weight: 500;
  }
`;

const CheckSlot = styled.span`
  display: inline-flex;
  height: 16px;
  margin-left: auto;
  padding-left: 8px;
  width: 24px;
`;

/** Non-interactive heading for a group of items inside a <Menu>. */
export const MenuLabel = styled.div`
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.025em;
  line-height: 16px;
  padding: 8px 10px 4px;
  text-transform: uppercase;
`;

/** Hairline between groups inside a <Menu>. */
export const MenuSeparator = styled.div.attrs({ role: 'separator' })`
  background-color: var(--border);
  flex-shrink: 0;
  height: 1px;
  margin: 4px -4px;
`;

const itemSelector = '[role="menuitem"], [role="menuitemradio"]';

/**
 * Small accessible dropdown menu (no dependency). Closes on outside click,
 * Escape and Tab; arrow keys, Home and End move between items.
 *
 *   <Menu
 *     align="end"
 *     renderTrigger={(triggerProps) => (
 *       <Button $variant="ghost" $size="icon" aria-label="Change theme" {...triggerProps}>
 *         <Sun size={20} strokeWidth={1.75} />
 *       </Button>
 *     )}
 *   >
 *     <MenuItem onSelect={() => setTheme('light')}>Light</MenuItem>
 *     <MenuItem href="/fr" checked>Français</MenuItem>
 *   </Menu>
 *
 * `renderTrigger` receives { ref, id, onClick, onKeyDown, aria-haspopup,
 * aria-expanded, aria-controls } to spread on a button. `align`: 'start' |
 * 'end' (default). `side`: 'bottom' (default) | 'top'. `onOpenChange(open)`.
 */
export const Menu = ({
  align = 'end',
  children,
  className,
  onOpenChange,
  renderTrigger,
  side = 'bottom',
}) => {
  const [open, setOpenState] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const setOpen = useCallback(
    (next) => {
      setOpenState(next);
      if (typeof onOpenChange === 'function') onOpenChange(next);
    },
    [onOpenChange]
  );

  const close = useCallback(
    ({ restoreFocus = true } = {}) => {
      setOpen(false);
      if (restoreFocus) triggerRef.current?.focus();
    },
    [setOpen]
  );

  // Focus the checked item (or the first one) when the menu opens.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const items = listRef.current.querySelectorAll(itemSelector);
    const current = listRef.current.querySelector('[aria-checked="true"], [aria-current="true"]');
    (current || items[0])?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open, setOpen]);

  const handleListKeyDown = (event) => {
    const items = Array.from(listRef.current?.querySelectorAll(itemSelector) || []);
    const index = items.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.stopPropagation();
        close();
        break;
      case 'Tab':
        close({ restoreFocus: false });
        break;
      case 'ArrowDown':
        event.preventDefault();
        items[(index + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
      default:
    }
  };

  const triggerProps = {
    ref: triggerRef,
    id: `${id}-trigger`,
    'aria-haspopup': 'menu',
    'aria-expanded': open ? 'true' : 'false',
    'aria-controls': open ? `${id}-menu` : undefined,
    onClick: () => setOpen(!open),
    onKeyDown: (event) => {
      if (event.key === 'ArrowDown' && !open) {
        event.preventDefault();
        setOpen(true);
      }
    },
  };

  return (
    <Wrapper className={className} ref={wrapperRef}>
      {renderTrigger(triggerProps)}
      {open && (
        <List
          $align={align}
          aria-labelledby={`${id}-trigger`}
          id={`${id}-menu`}
          onKeyDown={handleListKeyDown}
          ref={listRef}
          role="menu"
          $side={side}
        >
          <MenuContext.Provider value={{ close }}>{children}</MenuContext.Provider>
        </List>
      )}
    </Wrapper>
  );
};

/**
 * An entry in a <Menu>. Renders a link when `href` is set, otherwise a button.
 * `checked` (true / false) turns it into a radio item with a check mark;
 * `icon` is an optional leading icon. `onSelect` runs before the menu closes.
 */
export const MenuItem = ({ checked, children, href, icon, onSelect, ...props }) => {
  const { close } = useContext(MenuContext);
  const isRadio = typeof checked === 'boolean';

  const handleClick = (event) => {
    if (typeof onSelect === 'function') onSelect(event);
    // Leave focus alone when following a link: the page is about to change.
    close({ restoreFocus: !href });
  };

  return (
    <Item
      as={href ? 'a' : 'button'}
      aria-checked={isRadio ? (checked ? 'true' : 'false') : undefined}
      href={href}
      onClick={handleClick}
      role={isRadio ? 'menuitemradio' : 'menuitem'}
      tabIndex={-1}
      type={href ? undefined : 'button'}
      {...props}
    >
      {icon}
      <span>{children}</span>
      {isRadio && (
        <CheckSlot aria-hidden="true">{checked && <Check size={16} strokeWidth={2} />}</CheckSlot>
      )}
    </Item>
  );
};
